import {lang as cfgLang} from 'config';
import React, {useEffect, useState} from 'react';
import hoistStatics from 'hoist-non-react-statics';

import MarkdownIt from 'markdown-it';

const md = new MarkdownIt({
    html: true
});

const BASE_LANG = 'en';
const availableLanguagesPath = /^(es|fr|zh|it)$/i;
const availableLanguages = /^(en|es|fr|zh|it)$/i;

const detectUserLanguage = () => {
    // First detect from URL
    const urlLang = window.location.pathname.split('/').filter(it => it.length)[0];
    if (availableLanguagesPath.test(urlLang)) {
        return urlLang
    }
    // Then detect from the browser
    const browserLang = navigator.language.split('-')[0];
    if (availableLanguages.test(browserLang)) {
        return browserLang
    }
    return cfgLang;
}

const userLanguage = detectUserLanguage();

const MARKDOWN_MARKER = '!!!';

export const TranslationService = React.createContext({
    messages: null,
    currentLanguage: '',
    loadingLanguage: false,
    setLanguage: () => {},
    t: () => {}
});

TranslationService.Consumer.displayName = 'TranslationService';

export const useTranslationService = () => {
    const context = React.useContext(TranslationService);
    if (context === undefined) {
        throw new Error('useTranslationService must be used within a TranslationProvider');
    }
    return context;
};


export const TranslationProvider = ({initialLang = cfgLang, children}) => {

    const [messages, setMessages] = useState(null);
    const [currentLanguage, setCurrentLanguage] = useState(initialLang);
    const [loadingLanguage, setLoadingLanguage] = useState(false);

    const fixURLWithLangCode = langCode => {
        if (langCode !== currentLanguage) {
            const pathSplit = window.location.pathname.split("/").filter(it => it.length);
            if (availableLanguagesPath.test(pathSplit[0])) {
                pathSplit.shift()
            }
            let resultPath = ''
            if (langCode !== BASE_LANG) { // Base Language doesn't appear in URL
                resultPath += `/${langCode}`;
            }
            resultPath += `/${pathSplit.join("/")}`;
            window.history.replaceState(null, "", resultPath)
        }
    };

    const loadLanguage = (langCode) => {
        if (!loadingLanguage) {
            setLoadingLanguage(true);
            import(/* webpackChunkName: "i18n-[request]" */ `src/i18n/${langCode}`).then(module => {
                setMessages(module.default);
                setCurrentLanguage(langCode);
                setLoadingLanguage(false);
            }).catch(() => {
                import(/* webpackChunkName: "i18n-[request]" */ `src/i18n/${BASE_LANG}`).then(module => {
                    setMessages(module.default);
                    setCurrentLanguage(BASE_LANG);
                    setLoadingLanguage(false);
                });
            });
        }
    }

    const setLanguage = (langCode) => {
        fixURLWithLangCode(langCode);
        loadLanguage(langCode);
    };

    const t = (key, replacements = {}) => {

        if (messages === null) {
            return null;
        }

        let message = messages[key] !== undefined ? messages[key] : `_${key}_`;

        const startWithMark = message.startsWith(MARKDOWN_MARKER);

        if (startWithMark) {
            message = md.renderInline(message.replace(new RegExp('^'.concat(MARKDOWN_MARKER)), ''));
        }

        if (replacements && typeof replacements === 'object') {
            Object.keys(replacements)
                .forEach((key) => {
                    if (!Object.prototype.hasOwnProperty.call(replacements, key)) return;
                    const regexp = new RegExp(`:${key}\\b`, 'g');
                    message = message.replace(regexp, replacements[key].toString());
                });
        }

        if (startWithMark) {
            return (<span dangerouslySetInnerHTML={{
                __html: md.renderInline(message)
            }}/>);
        }

        return message;
    };

    useEffect(() => {
        loadLanguage(initialLang)
    }, [])

    const context = {
        messages, currentLanguage, loadingLanguage, setLanguage, t
    };

    return (<TranslationService.Provider value={context}>
        {children}
    </TranslationService.Provider>);
};

TranslationProvider.displayName = 'TranslationProvider';

export const withCopies = (Component) => {

    const displayName = 'withCopies(' + (Component.displayName || Component.name) + ')';

    const Wrapper = ({
                         wrappedComponentRef, ...rest
                     }) => React.createElement(TranslationService.Consumer, null, context => {
        const {
            currentLanguage, loadingLanguage, setLanguage, t
        } = context;
        return (React.createElement(Component, Object.assign({}, rest, {
            currentLanguage, loadingLanguage, setLanguage, t
        }, {
            ref: wrappedComponentRef
        })));
    });

    Wrapper.displayName = displayName;
    Wrapper.WrappedComponent = Component;

    return hoistStatics(Wrapper, Component);
};

export const withTranslationService = (_Component) => (<TranslationService.Consumer>
    {(translationService) => React.cloneElement(_Component, translationService)}
</TranslationService.Consumer>);
